import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import Img from "@insite/mobius/Img";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomLocationIcon from "@tSurya/Icons/CustomLocationIcon";
import MinusIcon from "@tSurya/Icons/MinusIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import uniqueId from "lodash/uniqueId";
import React, { useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabTitle = "tabTitle",
    desc = "desc",
    numberOfTabs = "numberOfTabs",
    faqBoxes = "faqBoxes",
    cssClass = "cssClass",
    image = "image",
    newImage = "newImage",
    eventLogo = "eventLogo",
    eventDate = "eventDate",
    eventName = "eventName",
    eventLocation = "eventLocation",
    regiterText = "regiterText",
    imgLink = "imgLink",
    imageAligment = "imageAligment",
    eventType = "eventType",
    detailPageLink = "detailPageLink",
}

interface NameArray {
    name: string;
    faqBoxes: FAQModel[];
}

interface TabModel {
    fields: NameArray;
}

interface FAQArray {
    questions: string;
    answers: string;
    newImage: string;
    eventLogo: string;
    imageAligment: string;
    eventType: string;
    eventDate: string;
    eventName: string;
    eventLocation: string;
    regiterText: string;
    imgLink: LinkFieldValue;
    detailPageLink: LinkFieldValue;
    openInNewWindow: boolean;
}

interface FAQModel {
    fields: FAQArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabTitle]: string;
        [fields.desc]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.faqBoxes]: FAQModel[];
        [fields.cssClass]: string;
        [fields.imgLink]: LinkFieldValue;
        [fields.eventType]: "Event" | "Webinars";
        [fields.imageAligment]: "left" | "right";
        [fields.detailPageLink]: LinkFieldValue;
    };
}

type Props = OwnProps;
export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    container?: InjectableCss;
    desc?: InjectableCss;
    imageStyles?: InjectableCss;
    logoStyles?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    accordianContainer?: InjectableCss;
    accordianSection?: AccordionSectionPresentationProps;
    imageContainer?: InjectableCss;
    imgLink?: InjectableCss;
    descriptionContainer?: InjectableCss;
    innerDetailsContainer?: InjectableCss;
    dateButton?: InjectableCss;
    eventName?: InjectableCss;
    eventLocation?: InjectableCss;
    registerBtn?: InjectableCss;
    locationContainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    button?: InjectableCss;
    selectedButton?: InjectableCss;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.background")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            background-color: ${COLORS.secondary.base};
            outline: 0;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            color: ${getColor("text.main")};
            cursor: pointer;
            :focus {
                outline: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="true"] {
                border-bottom: 2px solid ${COLORS.primary.base};
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.xlargest};
            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
            color: ${getColor("text.main")};
            /* identical to box height, or 117% */
            margin-bottom: 24px;
            text-align: center;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    desc: {
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 36px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            font-weight: ${TYPOGRAPHY.weight.base};
            color: ${getColor("text.main")};
        `,
    },
    imageStyles: {
        css: css`
            max-width: 520px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        max-width: 100%;
                        width: 100%;
                    `,
                    css`
                        max-width: 100%;
                        width: 100%;
                    `,
                    null,
                    null,
                ])}
            img {
                height: 100%;
                width: 100%;
                max-width: 520px;
                object-fit: contain;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 100%;
                            max-width: 100%;
                        `,
                        css`
                            width: 100%;
                            max-width: 100%;
                        `,
                        css`
                            width: 100%;
                            max-width: 100%;
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    logoStyles: {
        css: css`
            width: 100%;
            max-height: 81px;
            max-width: 200px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
            img {
                height: 100%;
                width: 100%;
                max-width: 200px;
                object-fit: cover;
                /* ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            max-width: 339px;
                        `,
                        null,
                        null,
                    ])} */
            }
        `,
    },
    container: {
        css: css`
            padding-bottom: 60px;
            padding-top: 52px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-top: 26px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-top: 26px;
                        padding-bottom: 30px;
                    `,
                    css`
                        padding-top: 26px;
                        padding-bottom: 30px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 21px;
                padding: 0px;
                z-index: 9;
                margin: 60px auto 30px;
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 400;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
            `,
            tabContent: css`
                border: none;
                max-width: 1060px;
                margin: 0 auto;
                padding: 60px 0px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 20px;
                        `,
                        css`
                            padding: 20px;
                        `,
                        css`
                            padding: 20px;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },

    accordianSection: {
        headerProps: {
            css: css`
                & button,
                & button:focus {
                    background-color: ${getColor("common.backgroundContrast")};
                    padding: 24px 20px;
                    border: 0;
                    > span:first-of-type {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 9px 20px 25px 40px;
                border: 0;
                &:not(dd[hidden]) {
                    display: flex;
                    display: flex;
                    align-items: center;
                }
                > span {
                    padding-left: 40px;
                    > p {
                        display: list-item;
                        margin-bottom: 0;
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.base};
                        color: ${getColor("text.accent")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        expandIconProps: {
            src: PlusIcon,
        },
        collapseIconProps: {
            src: MinusIcon,
        },
    },
    imageContainer: {
        css: css`
            display: block;
        `,
    },
    imgLink: {
        css: css`
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 18px;
                line-height: 26px;
                color: ${getColor("text.main")} !important;
                margin-top: 12px;
            }
        `,
    },
    descriptionContainer: {
        css: css`
            max-width: 400px;
            width: 100%;
            padding: 18px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        padding: 0px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 0px;
                    `,
                    css`
                        max-width: 100%;
                        padding: 0px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    innerDetailsContainer: {
        css: css`
            max-width: 100%;
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            row-gap: 18px;
            padding: 24px 22px 20px 22px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    css`
                        padding: 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    dateButton: {
        css: css`
            max-width: 200px;
            cursor: auto;
            background: ${COLORS.primary.base};
            border-color: ${COLORS.primary.base};
            color: ${COLORS.primary.contrast};
            &:hover,
            &:focus,
            &:active {
                background: ${COLORS.primary.base};
                border-color: ${COLORS.primary.base};
                color: ${COLORS.primary.contrast};
                box-shadow: none;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                    `,
                    css`
                        max-width: 100%;
                    `,
                    null,
                    null,
                    null,
                ])}
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 15px;
                line-height: 22px;
                text-align: center;
                color: ${COLORS.primary.contrast} !important;
                white-space: nowrap;
            }
        `,
    },
    eventName: {
        css: css`
            max-height: 51px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 18px;
            line-height: 26px;
            color: ${getColor("text.main")};
            p,
            span,
            div {
                font-family: inherit !important;
                color: inherit !important;
            }
            p {
                &:first-child {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 18px;
                    line-height: 26px;
                    margin-bottom: 0px;
                    color: ${getColor("text.main")};
                    text-align: left;
                }
                &:nth-child(2) {
                    margin-bottom: 0px;
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 400;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${COLORS.text.accent} !important;
                    text-align: left;
                }
            }
        `,
    },
    eventLocation: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${COLORS.text.accent};
        `,
    },
    registerBtn: {
        css: css`
            background-color: ${COLORS.background.main};
            border: 1px solid ${COLORS.primary.base};
            padding: 10px 34px;
            width: 100%;
            justify-content: center;
            max-width: 100%;
            margin: 0 auto;
            max-height: 44px;
            color: ${COLORS.text.main};
            &:hover,
            &:focus,
            &:active {
                background-color: ${COLORS.background.main};
                border-color: ${COLORS.primary.base};
                color: ${COLORS.text.main};
                box-shadow: none;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                color: ${getColor("text.main")} !important;
            }
        `,
    },

    locationContainer: {
        css: css`
            display: flex;
            min-height: 21px;
            gap: 10px;
        `,
    },
    buttonContainer: {
        css: css`
            display: flex;
            justify-content: center;
            column-gap: 20px;
            margin-bottom: 90px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 45px;
                    `,
                    css`
                        margin-bottom: 45px;
                    `,
                    css`
                        margin-bottom: 45px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    button: {
        css: css`
            padding: 8px 12px;
            background: ${COLORS.secondary.base};
            min-height: auto;
            border: none;
            transition: none !important;
            border-bottom: 2px solid transparent;
            color: ${COLORS.text.main};
            &:hover,
            &:focus,
            &:active {
                background: ${COLORS.secondary.base};
                border-color: transparent;
                color: ${COLORS.text.main};
                box-shadow: none;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 400;
                font-size: 15px;
                line-height: 24px;
                color: ${COLORS.text.main} !important;
            }
        `,
    },
    selectedButton: {
        css: css`
            padding: 8px 12px;
            background: ${COLORS.secondary.base};
            border: none;
            min-height: auto;
            transition: none !important;
            border-bottom: 2px solid ${COLORS.primary.base};
            color: ${COLORS.text.main};
            &:hover,
            &:focus,
            &:active {
                background: ${COLORS.secondary.base};
                border-bottom: 2px solid ${COLORS.primary.base};
                color: ${COLORS.text.main};
                box-shadow: none;
            }
            span {
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 15px;
                line-height: 24px;
                color: ${COLORS.text.main} !important;
            }
        `,
    },
};

const styles = tabularDataStyles;

const SurEventListPage: React.FC<Props> = ({ fields, id }) => {
    const redirectLink = useGetLinks(fields.faqBoxes, o => o.fields.detailPageLink).links;
    const [expandedAccordian, setExpandedAccordian] = React.useState<number | null>(null);

    const [active, setActive] = useState("all");

    const [result, setResult] = useState<any>([]);

    useEffect(() => {
        setResult(fields.faqBoxes);
    }, [fields.faqBoxes]);

    const activeHandler = (value: string) => {
        setActive(value);
    };

    const handleAllData = () => {
        setResult(fields.faqBoxes);
    };
    const handleEventFilter = (filter: string) => {
        const filteredData = fields.faqBoxes.filter(data => data.fields.eventType === filter);
        setResult(filteredData);
    };

    const accordianContainer = {
        css: css`
            display: flex;
            column-gap: 60px;
            max-width: 1060px;
            margin: 0 auto;
            margin-bottom: 120px;
            &:last-child {
                margin-bottom: 0px;
            }
            &:nth-child(odd) {
                flex-direction: row;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                    ])}
            }
            &:nth-child(even) {
                flex-direction: row-reverse;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        css`
                            flex-direction: column;
                        `,
                        null,
                        null,
                    ])}
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 60px;
                        flex-direction: column;
                    `,
                    css`
                        margin-bottom: 60px;
                        flex-direction: column;
                    `,
                    css`
                        margin-bottom: 60px;
                        flex-direction: column;
                    `,
                    null,
                    null,
                ])}
        `,
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.buttonContainer}>
                <Button
                    {...(active === "all" ? { ...styles.selectedButton } : { ...styles.button })}
                    onClick={() => {
                        handleAllData();
                        activeHandler("all");
                    }}
                    role="button"
                    aria-label="All"
                >
                    All
                </Button>
                {fields.faqBoxes.filter(data => data.fields.eventType === "Event").length > 0 && (
                    <Button
                        {...(active === "Event" ? { ...styles.selectedButton } : { ...styles.button })}
                        onClick={() => {
                            handleEventFilter("Event");
                            activeHandler("Event");
                        }}
                        role="button"
                        aria-label="Event"
                    >
                        Event
                    </Button>
                )}
                {fields.faqBoxes.filter(data => data.fields.eventType === "Webinars").length > 0 && (
                    <Button
                        {...(active === "Webinars" ? { ...styles.selectedButton } : { ...styles.button })}
                        onClick={() => {
                            handleEventFilter("Webinars");
                            activeHandler("Webinars");
                        }}
                        role="button"
                        aria-label="Webinars"
                    >
                        Webinars
                    </Button>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.imageContainer}>
                {result.map((faqs: FAQModel, index: number) => {
                    return (
                        <StyledWrapper {...accordianContainer} key={uniqueId()}>
                            <Link href={redirectLink[index].url} target={faqs.fields.openInNewWindow ? "_blank" : "_self"}>
                                <LazyImage src={faqs.fields.newImage} {...styles.imageStyles} altText="Event Image" />
                            </Link>
                            <StyledWrapper {...styles.descriptionContainer}>
                                <StyledWrapper {...styles.innerDetailsContainer}>
                                    <LazyImage src={faqs.fields.eventLogo} {...styles.logoStyles} altText="Event Logo" />
                                    <Button {...styles.dateButton} role="button" aria-label={faqs.fields.eventDate}>
                                        {faqs.fields.eventDate}
                                    </Button>
                                    <StyledWrapper {...styles.eventName}>{parse(faqs.fields.eventName, parserOptions)}</StyledWrapper>
                                    <StyledWrapper {...styles.locationContainer}>
                                        <Icon src={CustomLocationIcon} />
                                        <StyledWrapper {...styles.eventLocation}>{faqs.fields.eventLocation}</StyledWrapper>
                                    </StyledWrapper>

                                    <Link
                                        href={redirectLink[index].url}
                                        {...styles.registerBtn}
                                        target={faqs.fields.openInNewWindow ? `_blank` : `_self`}
                                        aria-label={faqs.fields.regiterText || "Registor Link"}
                                    >
                                        {faqs.fields.regiterText}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurEventListPage,
    definition: {
        group: "Common",
        displayName: "SurEventListPage",
        fieldDefinitions: [
            {
                name: fields.faqBoxes,
                displayName: "Event Boxes",
                editorTemplate: "ListField",
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.eventDate;
                },
                defaultValue: [],
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "eventType",
                        displayName: "Event Type",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Webinars", value: "Webinars" },
                            { displayName: "Event", value: "Event" },
                        ],
                        defaultValue: "",
                        sortOrder: 0,
                    },
                    {
                        displayName: "Event Image",
                        name: "newImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        displayName: "EventLogo",
                        name: "eventLogo",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "eventDate",
                        displayName: "Event Date",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "eventName",
                        editorTemplate: "RichTextField",
                        displayName: "Decription",
                        defaultValue: "",
                    },
                    {
                        name: "eventLocation",
                        displayName: "Event Location",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "regiterText",
                        displayName: "Button Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "detailPageLink",
                        editorTemplate: "LinkField",
                        displayName: "Redirect Link",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openInNewWindow",
                        displayName: "Open in New Window",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
